import dominique from "@app/assets/images/onboarding/dominique.webp";
import { useState } from "react";

import { Field, FunnelScreen, PrimaryButton } from "../screen";

import type { ScreenProps } from "../types";

// The screen the funnel opens on. It introduces Dominique before it asks for anything,
// and what it asks for is a name — which every question after this one uses, so
// the rest of the funnel can talk to a person rather than a visitor.
//
// The pair label is here rather than on the step because it is the label for two
// fields at once, and a step carries one per field. Those two are still the
// accessible names; this is what a sighted lead reads.
const PAIR_LABEL = "What's your name?";

export default function NameStep({
  step,
  answer,
  busy,
  save,
  back,
}: ScreenProps) {
  const fields = step.fields ?? [];
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      fields.map((f) => [
        f.key,
        answer?.fields.find((a) => a.key === f.key)?.value ?? "",
      ])
    )
  );
  const [submitted, setSubmitted] = useState(false);

  const errors: Record<string, string> = {};
  for (const field of fields) {
    if (!(values[field.key] ?? "").trim())
      errors[field.key] = `${field.label} cannot be blank`;
  }

  const submit = () => {
    setSubmitted(true);
    if (busy || Object.keys(errors).length > 0) return;
    save({
      fields: fields.map((f) => ({
        key: f.key,
        value: (values[f.key] ?? "").trim(),
      })),
    });
  };

  return (
    <FunnelScreen
      back={back}
      title={step.title}
      subtitle={step.subtitle}
      above={<Portrait />}
    >
      <form
        className="mt-6"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <p className="text-caption text-zinc-700">{PAIR_LABEL}</p>

        <div className="mt-1.5 grid grid-cols-2 gap-3">
          {fields.map((field) => (
            <Field
              key={field.key}
              field={field}
              hideLabel
              value={values[field.key] ?? ""}
              error={submitted ? errors[field.key] : undefined}
              onChange={(next) =>
                setValues((current) => ({ ...current, [field.key]: next }))
              }
            />
          ))}
        </div>

        <div className="mt-5">
          <PrimaryButton type="submit" disabled={busy}>
            Continue
          </PrimaryButton>
        </div>
      </form>
    </FunnelScreen>
  );
}

// The face behind the greeting, so "I'm Dominique" is said by someone.
function Portrait() {
  return (
    <img
      src={dominique}
      alt="Dominique Bouillet"
      width={64}
      height={64}
      className="mb-5 h-16 w-16 rounded-full object-cover ring-1 ring-zinc-200"
    />
  );
}
